import { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import QRCode from 'qrcode';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useLocalization } from '../contexts/LocalizationContext';
import { useAuth } from '../auth/AuthContext';
import { redeemCouponByPoints, checkCustomerExists } from '../api/auth';

import type { Coupon, PointRedemptionResponse } from '../types';
import { routes } from '../config';

interface Props {
  coupon: Coupon;
  onClose: () => void;
}

type Step = 'confirm' | 'loading' | 'success' | 'error';

export default function PointRedemptionDialog({ coupon, onClose }: Props) {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { updateCustomer } = useAuth();
  const [step, setStep] = useState<Step>('confirm');
  const [result, setResult] = useState<PointRedemptionResponse | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (result?.redemptionToken) {
      QRCode.toDataURL(`${result.redemptionToken}`, {
        width: 280, margin: 2, color: { dark: '#000', light: '#fff' },
      }).then(setQrDataUrl);
    }
  }, [result]);

  const handleConfirm = async () => {
    setStep('loading');
    try {
      const res = await redeemCouponByPoints(coupon.couponId);
      setResult(res);
      setStep('success');
      // Invalidate coupons so My Coupons shows fresh data immediately
      queryClient.invalidateQueries({ queryKey: ['coupons'] });
      // Refresh customer so points balance updates
      checkCustomerExists().then(r => {
        if (r.customer) updateCustomer(r.customer);
      }).catch(() => {});
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || t('pointRedemption.error.generic');
      setErrorMsg(msg);
      setStep('error');
    }
  };

  return (
    /* Overlay */
    <Box
      onClick={onClose}
      sx={{
        position: 'fixed', inset: 0, zIndex: 1000,
        backgroundColor: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      {/* Sheet */}
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          width: '88%', maxWidth: '360px',
          backgroundColor: '#fff',
          borderRadius: '20px',
          px: 3, pt: 3, pb: 3,
          boxShadow: '0 8px 40px rgba(0,0,0,0.28)',
        }}
      >
        {step === 'confirm' && (
          <>
            {/* Header row: emoji + name + pts */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '14px' }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: '12px', flexShrink: 0,
                background: 'linear-gradient(135deg,#E8F4EC,#C8E6D0)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px',
              }}>🎁</Box>
              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1A1A1A', lineHeight: 1.2 }}>
                  {coupon.name}
                </Typography>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#C4A574', mt: '2px' }}>
                  {(coupon.pointsToRedeem ?? 0) === 0
                    ? t('redeem.freeToClaim')
                    : `⭐ ${(coupon.pointsToRedeem ?? 0).toLocaleString()} ${t('pointRedemption.confirm.pts')}`}
                </Typography>
              </Box>
            </Box>

            {/* Divider */}
            <Box sx={{ height: '1px', background: '#F0F0F0', mb: '14px' }} />

            {/* Buttons */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Box
                component="button"
                onClick={onClose}
                sx={{
                  flex: 1, py: '13px', borderRadius: '10px', border: '1.5px solid #E0E0E0',
                  background: '#fff', color: '#6A6A6A', fontSize: '13px', fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit',
                  '&:active': { background: '#F5F5F5' },
                }}
              >
                {t('pointRedemption.confirm.cancel')}
              </Box>
              <Box
                component="button"
                onClick={handleConfirm}
                sx={{
                  flex: 1, py: '13px', borderRadius: '10px', border: 'none',
                  background: '#7BA88A', color: '#fff', fontSize: '13px', fontWeight: 700,
                  cursor: 'pointer', fontFamily: 'inherit',
                  '&:active': { background: '#6A9879' },
                }}
              >
                {t('pointRedemption.confirm.redeem')}
              </Box>
            </Box>
          </>
        )}

        {step === 'loading' && (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <CircularProgress sx={{ color: '#7BA88A', mb: 2 }} />
            <Typography sx={{ fontSize: '15px', color: '#6A6A6A' }}>
              {t('pointRedemption.loading')}
            </Typography>
          </Box>
        )}

        {step === 'success' && (
          <>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Box sx={{
                width: 52, height: 52, borderRadius: '50%', backgroundColor: '#EAF4EE',
                display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5,
              }}>
                <svg viewBox="0 0 24 24" width="26" height="26" fill="none">
                  <path d="M5 12l5 5L19 7" stroke="#7BA88A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Box>
              <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#1A1A1A', mb: '4px' }}>
                {t('pointRedemption.success.title')}
              </Typography>
              <Typography sx={{ fontSize: '13px', color: '#9A9A9A' }}>
                {t('pointRedemption.success.hint')}
              </Typography>
            </Box>

            {qrDataUrl && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Box
                  component="img"
                  src={qrDataUrl}
                  alt="Redemption QR"
                  sx={{ width: 200, height: 200, borderRadius: '12px' }}
                />
              </Box>
            )}

            <Typography sx={{ fontSize: '13px', color: '#9A9A9A', textAlign: 'center', mb: 3, lineHeight: 1.5 }}>
              {t('pointRedemption.success.qrInstruction')}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Box
                component="button"
                onClick={() => { onClose(); navigate(routes.coupons); }}
                sx={{
                  flex: 1, py: '14px', borderRadius: '12px', border: '1.5px solid #E0E0E0',
                  background: '#fff', color: '#6A6A6A', fontSize: '14px', fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit',
                  '&:active': { background: '#F5F5F5' },
                }}
              >
                {t('pointRedemption.success.myCoupons')}
              </Box>
              <Box
                component="button"
                onClick={() => { onClose(); navigate(routes.couponDetail, { state: { couponId: coupon.couponId } }); }}
                sx={{
                  flex: 1, py: '14px', borderRadius: '12px', border: 'none',
                  background: '#7BA88A', color: '#fff', fontSize: '14px', fontWeight: 700,
                  cursor: 'pointer', fontFamily: 'inherit',
                  '&:active': { background: '#6A9879' },
                }}
              >
                {t('pointRedemption.success.useNow')}
              </Box>
            </Box>
          </>
        )}

        {step === 'error' && (
          <>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Box sx={{
                width: 52, height: 52, borderRadius: '50%', backgroundColor: '#FEF0EE',
                display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5,
              }}>
                <svg viewBox="0 0 24 24" width="26" height="26" fill="none">
                  <path d="M12 8v5M12 16h.01" stroke="#E85A22" strokeWidth="2" strokeLinecap="round" />
                  <circle cx="12" cy="12" r="9" stroke="#E85A22" strokeWidth="1.8" />
                </svg>
              </Box>
              <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#1A1A1A', mb: '6px' }}>
                {t('pointRedemption.error.title')}
              </Typography>
              <Typography sx={{ fontSize: '13px', color: '#9A9A9A', lineHeight: 1.5 }}>
                {errorMsg}
              </Typography>
            </Box>
            <Box
              component="button"
              onClick={onClose}
              sx={{
                width: '100%', py: '14px', borderRadius: '12px', border: 'none',
                background: '#7BA88A', color: '#fff', fontSize: '15px', fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit',
                '&:active': { background: '#6A9879' },
              }}
            >
              {t('pointRedemption.error.close')}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
